import React, { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { NotificationChannelCard } from '../components/NotificationChannelCard';
import { ProfilePhotoUploadModal } from '../components/ProfilePhotoUploadModal';
import type { NotificationPreferences } from '../types';
import type { AppTheme, SettingsScreenPalette } from '../theme/appearanceTokens';
import { getAppearanceTokens } from '../theme/appearanceTokens';

export type AppLanguage = 'en' | 'es';
export type { AppTheme } from '../theme/appearanceTokens';

const LANGUAGE_OPTIONS: Array<{ readonly value: AppLanguage; readonly label: string }> = [
  { value: 'en', label: 'English' },
];

const THEME_OPTIONS: Array<{ readonly value: AppTheme; readonly label: string }> = [
  { value: 'light', label: 'Light' },
];

interface SettingsScreenProps {
  readonly displayName: string;
  readonly email: string;
  readonly avatarUrl: string | null;
  readonly language: AppLanguage;
  readonly theme: AppTheme;
  readonly onLanguageChange: (language: AppLanguage) => Promise<void> | void;
  readonly onThemeChange: (theme: AppTheme) => Promise<void> | void;
  readonly onPhotoChange: (payload: {
    uri: string;
    mimeType: string;
  }) => Promise<void> | void;
  readonly onPhotoRemove: () => Promise<void> | void;
  readonly notificationPreferences: NotificationPreferences;
  readonly onNotificationPreferencesChange: (
    input: Partial<NotificationPreferences>
  ) => Promise<void> | void;
}

const PAGE_MAX_WIDTH = 700;

function createSettingsStyles(p: SettingsScreenPalette) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: p.pageBackground,
    },
    contentContainer: {
      paddingHorizontal: 20,
      paddingTop: 20,
      paddingBottom: 32,
      alignItems: 'flex-start',
    },
    pageInner: {
      width: '100%',
      maxWidth: PAGE_MAX_WIDTH,
      alignSelf: 'flex-start',
    },
    title: {
      fontSize: 26,
      color: p.title,
      fontFamily: 'Montserrat_700Bold',
      marginBottom: 20,
      alignSelf: 'flex-start',
    },
    card: {
      width: '100%',
      backgroundColor: p.cardBackground,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: p.cardBorder,
      marginBottom: 16,
      overflow: 'hidden',
      shadowColor: p.cardShadowColor,
      shadowOpacity: p.cardShadowOpacity,
      shadowRadius: 20,
      shadowOffset: { width: 0, height: 4 },
      elevation: 8,
    },
    cardHeader: {
      paddingHorizontal: 16,
      paddingVertical: 12,
    },
    cardHeaderRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingVertical: 12,
      minHeight: 52,
    },
    sectionHeaderRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: 42,
      marginTop: 10,
      marginBottom: 4,
    },
    cardHeaderTitle: {
      fontSize: 15,
      color: p.cardHeaderTitle,
      fontFamily: 'Montserrat_600SemiBold',
    },
    cardDivider: {
      height: 1,
      backgroundColor: p.divider,
    },
    cardHint: {
      fontSize: 12,
      color: p.cardHint,
      fontFamily: 'Montserrat_500Medium',
      lineHeight: 17,
      paddingHorizontal: 16,
      paddingTop: 12,
      paddingBottom: 4,
    },
    profileBody: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingHorizontal: 16,
      paddingTop: 12,
      paddingBottom: 16,
    },
    profileBodyStacked: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
    avatarColumn: {
      width: 184,
      alignItems: 'center',
      paddingRight: 28,
    },
    avatarColumnStacked: {
      width: '100%',
      paddingRight: 0,
      marginBottom: 16,
      alignItems: 'center',
    },
    profileDataColumn: {
      flex: 1,
      minWidth: 0,
      paddingLeft: 16,
    },
    profileDataColumnStacked: {
      width: '100%',
      flex: 0,
      paddingLeft: 0,
    },
    prefsBody: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingHorizontal: 16,
      paddingTop: 12,
      paddingBottom: 16,
    },
    prefsBodyStacked: {
      flexDirection: 'column',
    },
    comboHalf: {
      flex: 1,
      minWidth: 0,
      paddingHorizontal: 6,
    },
    comboHalfStacked: {
      width: '100%',
      flex: 0,
      alignSelf: 'stretch',
      paddingHorizontal: 0,
      marginBottom: 18,
    },
    comboHalfStackedLast: {
      marginBottom: 0,
    },
    profileAvatar: {
      width: 96,
      height: 96,
      borderRadius: 48,
      backgroundColor: p.avatarPlaceholder,
    },
    profileAvatarFallback: {
      width: 96,
      height: 96,
      borderRadius: 48,
      backgroundColor: p.avatarFallback,
      alignItems: 'center',
      justifyContent: 'center',
    },
    profileAvatarLetter: {
      fontSize: 30,
      fontWeight: '600',
      color: p.avatarLetter,
      fontFamily: 'Montserrat_600SemiBold',
    },
    photoStubButton: {
      alignSelf: 'center',
      marginTop: 14,
      minWidth: 140,
      paddingHorizontal: 16,
      paddingVertical: 10,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: p.photoButtonBorder,
      backgroundColor: p.photoButtonBackground,
      alignItems: 'center',
      justifyContent: 'center',
      ...Platform.select({
        web: {
          // @ts-ignore — evita salto de línea en el texto del botón
          whiteSpace: 'nowrap',
        },
        default: {},
      }),
    },
    photoStubButtonText: {
      fontSize: 13,
      color: p.photoButtonText,
      fontFamily: 'Montserrat_600SemiBold',
    },
    photoRemoveButton: {
      alignSelf: 'center',
      marginTop: 8,
      minWidth: 140,
      paddingHorizontal: 16,
      paddingVertical: 10,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: p.readOnlyBoxBorder,
      backgroundColor: p.readOnlyBoxBackground,
      alignItems: 'center',
      justifyContent: 'center',
    },
    photoRemoveButtonText: {
      fontSize: 13,
      color: p.cardHint,
      fontFamily: 'Montserrat_600SemiBold',
    },
    readOnlyField: {
      marginBottom: 18,
    },
    readOnlyFieldLastInColumn: {
      marginBottom: 0,
    },
    readOnlyLabel: {
      marginBottom: 6,
      fontSize: 13,
      color: p.readOnlyLabel,
      fontFamily: 'Montserrat_600SemiBold',
    },
    readOnlyValueBox: {
      minHeight: 44,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: p.readOnlyBoxBorder,
      backgroundColor: p.readOnlyBoxBackground,
      paddingHorizontal: 12,
      paddingVertical: 11,
      justifyContent: 'center',
    },
    readOnlyValue: {
      fontSize: 14,
      color: p.readOnlyValue,
      fontFamily: 'Montserrat_500Medium',
    },
    sectionLabel: {
      marginBottom: 10,
      fontSize: 14,
      color: p.sectionLabel,
      fontFamily: 'Montserrat_600SemiBold',
    },
    selectorTrigger: {
      minHeight: 44,
      borderWidth: 1,
      borderColor: p.selectorBorder,
      borderRadius: 10,
      paddingHorizontal: 12,
      paddingVertical: 10,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: p.selectorBackground,
    },
    selectorValue: {
      fontSize: 14,
      color: p.selectorValue,
      fontFamily: 'Montserrat_500Medium',
    },
    selectorChevron: {
      fontSize: 12,
      color: p.selectorChevron,
      fontFamily: 'Montserrat_600SemiBold',
    },
    selectorOptions: {
      marginTop: 8,
      borderWidth: 1,
      borderColor: p.selectorOptionsBorder,
      borderRadius: 10,
      backgroundColor: p.selectorOptionsBackground,
      overflow: 'hidden',
      zIndex: 20,
      elevation: 12,
    },
    selectorOption: {
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderBottomWidth: 1,
      borderBottomColor: p.selectorOptionDivider,
    },
    selectorOptionText: {
      fontSize: 14,
      color: p.selectorOptionText,
      fontFamily: 'Montserrat_500Medium',
    },
    notificationBody: {
      paddingHorizontal: 16,
      paddingTop: 12,
      paddingBottom: 16,
    },
    notificationMicrocopy: {
      fontSize: 12,
      color: p.cardHint,
      fontFamily: 'Montserrat_500Medium',
      lineHeight: 17,
      marginBottom: 10,
    },
    notificationRow: {
      minHeight: 48,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderBottomWidth: 1,
      borderBottomColor: p.divider,
    },
    notificationRowLast: {
      borderBottomWidth: 0,
    },
    notificationGroupTitle: {
      fontSize: 13,
      color: p.cardHint,
      fontFamily: 'Montserrat_600SemiBold',
      marginTop: 10,
      marginBottom: 4,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
    },
    notificationNestedRow: {
      marginLeft: 22,
    },
    notificationSubSection: {
      marginTop: 2,
    },
    notificationSubSectionDisabled: {
      opacity: 0.5,
    },
    notificationLabel: {
      fontSize: 14,
      color: p.readOnlyValue,
      fontFamily: 'Montserrat_500Medium',
      flex: 1,
      marginRight: 12,
    },
    checkboxRow: {
      minHeight: 38,
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 6,
    },
    checkboxContent: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
      minWidth: 0,
    },
    checkboxBox: {
      width: 18,
      height: 18,
      borderRadius: 4,
      borderWidth: 1,
      borderColor: p.selectorBorder,
      backgroundColor: p.selectorBackground,
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 10,
    },
    checkboxBoxChecked: {
      backgroundColor: '#2563EB',
      borderColor: '#2563EB',
    },
    checkboxMark: {
      color: '#FFFFFF',
      fontSize: 11,
      fontFamily: 'Montserrat_700Bold',
      lineHeight: 12,
    },
    checkboxLabel: {
      flex: 1,
      fontSize: 14,
      color: p.readOnlyValue,
      fontFamily: 'Montserrat_500Medium',
    },
    inlineInputWrapper: {
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: 4,
      marginBottom: 8,
    },
    inlineInputLabel: {
      fontSize: 13,
      color: p.readOnlyValue,
      fontFamily: 'Montserrat_600SemiBold',
      marginRight: 8,
      minWidth: 42,
    },
    previewButton: {
      alignSelf: 'flex-start',
      minHeight: 40,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: p.selectorBorder,
      backgroundColor: p.selectorBackground,
      paddingHorizontal: 14,
      justifyContent: 'center',
      marginBottom: 10,
    },
    previewButtonText: {
      fontSize: 13,
      color: p.selectorValue,
      fontFamily: 'Montserrat_600SemiBold',
    },
    destructiveButton: {
      alignSelf: 'flex-start',
      minHeight: 40,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: '#FCA5A5',
      backgroundColor: '#FEF2F2',
      paddingHorizontal: 14,
      justifyContent: 'center',
    },
    destructiveButtonText: {
      fontSize: 13,
      color: '#B91C1C',
      fontFamily: 'Montserrat_600SemiBold',
    },
    customDaysInput: {
      minWidth: 88,
      height: 38,
      borderWidth: 1,
      borderColor: p.selectorBorder,
      borderRadius: 8,
      backgroundColor: p.selectorBackground,
      color: p.selectorValue,
      fontSize: 14,
      fontFamily: 'Montserrat_500Medium',
      textAlign: 'center',
      paddingHorizontal: 8,
      paddingVertical: 6,
    },
  });
}

export const SettingsScreen: React.FC<SettingsScreenProps> = ({
  displayName,
  email,
  avatarUrl,
  language,
  theme,
  onLanguageChange,
  onThemeChange,
  onPhotoChange,
  onPhotoRemove,
  notificationPreferences,
  onNotificationPreferencesChange,
}) => {
  const palette = useMemo(() => getAppearanceTokens(theme).settings, [theme]);
  const styles = useMemo(() => createSettingsStyles(palette), [palette]);

  const { width: windowWidth } = useWindowDimensions();
  const isNarrowLayout = windowWidth < 640;

  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const [isPhotoModalVisible, setIsPhotoModalVisible] = useState(false);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  useEffect(() => {
    setAvatarLoadFailed(false);
  }, [avatarUrl]);

  const userInitial = useMemo(() => {
    const normalized = displayName.trim();
    if (!normalized) {
      return 'U';
    }
    return normalized.charAt(0).toUpperCase();
  }, [displayName]);

  const emailDisplay = email.trim().length > 0 ? email.trim() : '—';
  const selectedLanguageLabel =
    LANGUAGE_OPTIONS.find(option => option.value === language)?.label || 'English';
  const selectedThemeLabel = THEME_OPTIONS.find(option => option.value === theme)?.label || 'Light';

  const mapChannelPatchToPreferences = (
    patch: Partial<{
      maturityEnabled: boolean;
      autocallEnabled: boolean;
      issuerCallEnabled: boolean;
      couponEnabled: boolean;
      barrierHitEnabled: boolean;
      sameDayEnabled: boolean;
      sevenDaysEnabled: boolean;
      oneDayEnabled: boolean;
      customEnabled: boolean;
      customDays: number;
    }>,
    keys: {
      maturityEnabled: keyof NotificationPreferences;
      autocallEnabled: keyof NotificationPreferences;
      issuerCallEnabled: keyof NotificationPreferences;
      couponEnabled: keyof NotificationPreferences;
      barrierHitEnabled: keyof NotificationPreferences;
      sameDayEnabled: keyof NotificationPreferences;
      oneDayEnabled: keyof NotificationPreferences;
      sevenDaysEnabled: keyof NotificationPreferences;
      customEnabled: keyof NotificationPreferences;
      customDays: keyof NotificationPreferences;
    }
  ): Partial<NotificationPreferences> => {
    const mapped: Partial<NotificationPreferences> = {};
    (Object.keys(patch) as Array<keyof typeof keys>).forEach(field => {
      const value = patch[field];
      if (value !== undefined) {
        (mapped as Record<string, boolean | number>)[keys[field]] = value;
      }
    });
    return mapped;
  };

  return (
    <Fragment>
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <View style={styles.pageInner}>
        <Text style={styles.title}>Settings</Text>

        <View style={styles.card}>
          <View style={styles.cardHeader}>
            <Text style={styles.cardHeaderTitle}>Profile</Text>
          </View>
          <View style={styles.cardDivider} />
          <Text style={styles.cardHint}>
            Your name and email are managed by your administrator.
          </Text>

          <View style={[styles.profileBody, isNarrowLayout && styles.profileBodyStacked]}>
            <View style={[styles.avatarColumn, isNarrowLayout && styles.avatarColumnStacked]}>
              {avatarUrl && !avatarLoadFailed ? (
                <Image
                  source={{ uri: avatarUrl }}
                  style={styles.profileAvatar}
                  resizeMode="cover"
                  onError={() => setAvatarLoadFailed(true)}
                />
              ) : (
                <View style={styles.profileAvatarFallback}>
                  <Text style={styles.profileAvatarLetter}>{userInitial}</Text>
                </View>
              )}
              <TouchableOpacity
                style={styles.photoStubButton}
                activeOpacity={0.85}
                onPress={() => setIsPhotoModalVisible(true)}
                accessibilityRole="button"
                accessibilityLabel="Change profile photo"
              >
                <Text style={styles.photoStubButtonText} numberOfLines={1}>
                  Change photo
                </Text>
              </TouchableOpacity>
              {avatarUrl && (
                <TouchableOpacity
                  style={styles.photoRemoveButton}
                  activeOpacity={0.85}
                  onPress={() => void onPhotoRemove()}
                  accessibilityRole="button"
                  accessibilityLabel="Remove profile photo"
                >
                  <Text style={styles.photoRemoveButtonText} numberOfLines={1}>
                    Remove photo
                  </Text>
                </TouchableOpacity>
              )}
            </View>

            <View
              style={[
                styles.profileDataColumn,
                isNarrowLayout && styles.profileDataColumnStacked,
                isNarrowLayout && {
                  borderBottomWidth: 1,
                  borderBottomColor: palette.profileSeparator,
                  paddingBottom: 20,
                  marginBottom: 20,
                },
              ]}
            >
              <View style={styles.readOnlyField}>
                <Text style={styles.readOnlyLabel}>Full name</Text>
                <View style={styles.readOnlyValueBox}>
                  <Text style={styles.readOnlyValue} numberOfLines={3}>
                    {displayName}
                  </Text>
                </View>
              </View>

              <View style={[styles.readOnlyField, styles.readOnlyFieldLastInColumn]}>
                <Text style={styles.readOnlyLabel}>Email</Text>
                <View style={styles.readOnlyValueBox}>
                  <Text style={styles.readOnlyValue} numberOfLines={3}>
                    {emailDisplay}
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.card}>
          <View style={styles.cardHeader}>
            <Text style={styles.cardHeaderTitle}>Preferences</Text>
          </View>
          <View style={styles.cardDivider} />
          <View style={[styles.prefsBody, isNarrowLayout && styles.prefsBodyStacked]}>
          <View
            style={[
              styles.comboHalf,
              isNarrowLayout && styles.comboHalfStacked,
            ]}
          >
            <Text style={styles.sectionLabel}>Language</Text>
            <TouchableOpacity
              style={[
                styles.selectorTrigger,
                isLanguageOpen && {
                  borderColor: palette.selectorFocusBorder,
                },
              ]}
              activeOpacity={0.8}
              onPress={() => {
                setIsLanguageOpen(previousValue => !previousValue);
                setIsThemeOpen(false);
              }}
            >
              <Text style={styles.selectorValue} numberOfLines={1}>
                {selectedLanguageLabel}
              </Text>
              <Text style={styles.selectorChevron}>{isLanguageOpen ? '▴' : '▾'}</Text>
            </TouchableOpacity>

            {isLanguageOpen && (
              <View style={styles.selectorOptions}>
                {LANGUAGE_OPTIONS.map(option => (
                  <TouchableOpacity
                    key={option.value}
                    style={styles.selectorOption}
                    activeOpacity={0.7}
                    onPress={() => {
                      void onLanguageChange(option.value);
                      setIsLanguageOpen(false);
                    }}
                  >
                    <Text style={styles.selectorOptionText}>{option.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>

          <View
            style={[
              styles.comboHalf,
              isNarrowLayout && styles.comboHalfStacked,
              isNarrowLayout && styles.comboHalfStackedLast,
            ]}
          >
            <Text style={styles.sectionLabel}>Theme</Text>
            <TouchableOpacity
              style={[
                styles.selectorTrigger,
                isThemeOpen && {
                  borderColor: palette.selectorFocusBorder,
                },
              ]}
              activeOpacity={0.8}
              onPress={() => {
                setIsThemeOpen(previousValue => !previousValue);
                setIsLanguageOpen(false);
              }}
            >
              <Text style={styles.selectorValue} numberOfLines={1}>
                {selectedThemeLabel}
              </Text>
              <Text style={styles.selectorChevron}>{isThemeOpen ? '▴' : '▾'}</Text>
            </TouchableOpacity>

            {isThemeOpen && (
              <View style={styles.selectorOptions}>
                {THEME_OPTIONS.map(option => (
                  <TouchableOpacity
                    key={option.value}
                    style={styles.selectorOption}
                    activeOpacity={0.7}
                    onPress={() => {
                      void onThemeChange(option.value);
                      setIsThemeOpen(false);
                    }}
                  >
                    <Text style={styles.selectorOptionText}>{option.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>
          </View>
        </View>

        <NotificationChannelCard
          title="In-App Notifications"
          palette={palette}
          timingMode="exclusiveHorizon"
          masterEnabled={notificationPreferences.inAppEnabled}
          values={{
            maturityEnabled: notificationPreferences.expiryEnabled,
            autocallEnabled: notificationPreferences.keyDatesAutocallEnabled,
            issuerCallEnabled: notificationPreferences.keyDatesCallWindowEnabled,
            couponEnabled: notificationPreferences.keyDatesCouponEnabled,
            barrierHitEnabled: notificationPreferences.keyDatesBarrierEnabled,
            sameDayEnabled: notificationPreferences.keyDatesSameDayEnabled,
            oneDayEnabled: notificationPreferences.keyDates1dEnabled,
            sevenDaysEnabled: notificationPreferences.keyDates7dEnabled,
            customEnabled: notificationPreferences.keyDatesCustomEnabled,
            customDays: notificationPreferences.keyDatesCustomDays,
          }}
          onApply={({ masterEnabled, values }) =>
            void onNotificationPreferencesChange({
              inAppEnabled: masterEnabled,
              ...mapChannelPatchToPreferences(values, {
                maturityEnabled: 'expiryEnabled',
                autocallEnabled: 'keyDatesAutocallEnabled',
                issuerCallEnabled: 'keyDatesCallWindowEnabled',
                couponEnabled: 'keyDatesCouponEnabled',
                barrierHitEnabled: 'keyDatesBarrierEnabled',
                sameDayEnabled: 'keyDatesSameDayEnabled',
                oneDayEnabled: 'keyDates1dEnabled',
                sevenDaysEnabled: 'keyDates7dEnabled',
                customEnabled: 'keyDatesCustomEnabled',
                customDays: 'keyDatesCustomDays',
              }),
            })
          }
        />

        <NotificationChannelCard
          title="Email Notifications"
          palette={palette}
          timingMode="exactDays"
          masterEnabled={notificationPreferences.emailEnabled}
          values={{
            maturityEnabled: notificationPreferences.emailExpiryEnabled,
            autocallEnabled: notificationPreferences.emailKeyDatesAutocallEnabled,
            issuerCallEnabled: notificationPreferences.emailKeyDatesIssuerCallEnabled,
            couponEnabled: notificationPreferences.emailKeyDatesCouponEnabled,
            barrierHitEnabled: notificationPreferences.emailBarrierHitEnabled,
            sameDayEnabled: notificationPreferences.emailKeyDatesSameDayEnabled,
            oneDayEnabled: notificationPreferences.emailKeyDates1dEnabled,
            sevenDaysEnabled: notificationPreferences.emailKeyDates7dEnabled,
            customEnabled: notificationPreferences.emailKeyDatesCustomEnabled,
            customDays: notificationPreferences.emailKeyDatesCustomDays,
          }}
          onApply={({ masterEnabled, values }) =>
            void onNotificationPreferencesChange({
              emailEnabled: masterEnabled,
              ...mapChannelPatchToPreferences(values, {
                maturityEnabled: 'emailExpiryEnabled',
                autocallEnabled: 'emailKeyDatesAutocallEnabled',
                issuerCallEnabled: 'emailKeyDatesIssuerCallEnabled',
                couponEnabled: 'emailKeyDatesCouponEnabled',
                barrierHitEnabled: 'emailBarrierHitEnabled',
                sameDayEnabled: 'emailKeyDatesSameDayEnabled',
                oneDayEnabled: 'emailKeyDates1dEnabled',
                sevenDaysEnabled: 'emailKeyDates7dEnabled',
                customEnabled: 'emailKeyDatesCustomEnabled',
                customDays: 'emailKeyDatesCustomDays',
              }),
            })
          }
        />

        <View style={styles.card}>
          <View style={styles.cardHeader}>
            <Text style={styles.cardHeaderTitle}>Notification Retention</Text>
          </View>
          <View style={styles.cardDivider} />
          <View style={styles.notificationBody}>
            <View style={styles.notificationRow}>
              <Text style={styles.notificationLabel}>Delete read notifications</Text>
              <Switch
                value={notificationPreferences.cleanupReadEnabled}
                onValueChange={(value: boolean) => void onNotificationPreferencesChange({ cleanupReadEnabled: value })}
                trackColor={{ false: '#D1D5DB', true: '#BFDBFE' }}
                thumbColor={notificationPreferences.cleanupReadEnabled ? '#2563EB' : '#FFFFFF'}
              />
            </View>
            <View style={styles.notificationRow}>
              <Text style={styles.notificationLabel}>Delete unread notifications</Text>
              <Switch
                value={notificationPreferences.cleanupUnreadEnabled}
                onValueChange={(value: boolean) =>
                  void onNotificationPreferencesChange({ cleanupUnreadEnabled: value })
                }
                trackColor={{ false: '#D1D5DB', true: '#BFDBFE' }}
                thumbColor={notificationPreferences.cleanupUnreadEnabled ? '#2563EB' : '#FFFFFF'}
              />
            </View>

            {(notificationPreferences.cleanupReadEnabled ||
              notificationPreferences.cleanupUnreadEnabled) && (
              <View style={[styles.notificationRow, styles.notificationRowLast]}>
                <Text style={styles.notificationLabel}>Retention period (days)</Text>
                <TextInput
                  value={String(notificationPreferences.cleanupRetentionDays)}
                  keyboardType="number-pad"
                  style={styles.customDaysInput}
                  placeholder="14"
                  placeholderTextColor="#9CA3AF"
                  onChangeText={(value: string) => {
                    const onlyDigits = value.replace(/[^0-9]/g, '');
                    if (!onlyDigits) {
                      void onNotificationPreferencesChange({ cleanupRetentionDays: 14 });
                      return;
                    }
                    const parsed = Number(onlyDigits);
                    const nextValue = Number.isFinite(parsed)
                      ? Math.max(1, Math.min(365, Math.floor(parsed)))
                      : 14;
                    void onNotificationPreferencesChange({ cleanupRetentionDays: nextValue });
                  }}
                />
              </View>
            )}
          </View>
        </View>

      </View>
    </ScrollView>
    <ProfilePhotoUploadModal
      visible={isPhotoModalVisible}
      theme={theme}
      onClose={() => setIsPhotoModalVisible(false)}
      onValidationSuccess={async payload => {
        await onPhotoChange({
          uri: payload.uri,
          mimeType: payload.mimeType,
        });
      }}
    />
    </Fragment>
  );
};
